import type { PasswordRequirementItem, PasswordRequirementStatus } from '../../lib/passwordPolicy';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';

interface PasswordRequirementsProps {
  items: PasswordRequirementItem[];
  /** Shown under the list, e.g. which rules are only checked on save. */
  note?: string;
  /** For `aria-describedby` on the password input. */
  id?: string;
}

const TEXT_CLASS: Record<PasswordRequirementStatus, string> = {
  met: 'text-green-700 dark:text-green-400',
  unmet: 'text-red-600 dark:text-red-400',
  checking: 'text-muted-foreground',
  pending: 'text-muted-foreground',
};

const SCREEN_READER_STATUS: Record<PasswordRequirementStatus, string> = {
  met: 'met',
  unmet: 'not met',
  checking: 'checking',
  pending: 'not yet met',
};

function StatusIcon({ status }: { status: PasswordRequirementStatus }) {
  if (status === 'met') return <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />;
  if (status === 'unmet') return <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />;
  if (status === 'checking') return <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />;
  return (
    <span className="flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden="true">
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
    </span>
  );
}

/**
 * CR-8598: the platform password policy as a live checklist.
 *
 * Each row ticks green once met, turns red when the typed password breaks it,
 * and spins while the server is still checking it. Callers decide each row's
 * status (see `lib/passwordPolicy.ts`); this only renders.
 *
 * @param items - The requirements, in display order, with their status
 * @param note - Optional line under the list
 * @param id - Element id, for the input's `aria-describedby`
 */
export function PasswordRequirements({ items, note, id }: PasswordRequirementsProps) {
  return (
    <div id={id} className="rounded-lg bg-muted p-3 text-sm space-y-1.5" aria-live="polite">
      <p className="font-medium text-foreground">Password requirements:</p>
      <ul className="space-y-1">
        {items.map((item) => (
          <li
            key={item.key}
            data-requirement={item.key}
            data-status={item.status}
            className={`flex items-center gap-2 ${TEXT_CLASS[item.status]}`}
          >
            <StatusIcon status={item.status} />
            <span>{item.label}</span>
            <span className="sr-only"> — {SCREEN_READER_STATUS[item.status]}</span>
          </li>
        ))}
      </ul>
      {note && <p className="pt-1 text-xs text-muted-foreground">{note}</p>}
    </div>
  );
}
